/**
 * Codes des refus de l'API (docs/API.md § Codes d'erreur). Le code est stable
 * et sert au téléphone à classer un refus de sa file d'envoi ; le message, en
 * français, s'affiche tel quel. Tous les refus codés ont le même corps.
 */
/** Dépôt de CV et page « CV à rattacher » (docs/API.md § CV à rattacher). */
export const CODES_ERREUR_CV = [
  'CV_FICHIER_ABSENT', 'CV_FORMAT_REFUSE', 'CV_TROP_VOLUMINEUX', 'CV_ENVOI_INVALIDE', 'CV_DEJA_PRESENT',
  'DEPOT_CV_INTROUVABLE', 'DEPOT_CV_DEJA_TRAITE', 'DEPOT_CV_NON_RATTACHE',
  'COORDONNEE_NON_LUE', 'COORDONNEE_DEJA_RENSEIGNEE', 'COORDONNEE_AUTRE_FICHE',
  'ANNONCE_NON_OUVERTE', 'PERSONNE_NE_PLUS_CONTACTER', 'COORDONNEES_DEUX_FICHES',
  'CANDIDATURE_EMBAUCHEE',
] as const;
export type CodeErreurCv = (typeof CODES_ERREUR_CV)[number];

/** Formulaire public (docs/FORMULAIRE_PUBLIC.md § 4), routes sans authentification. */
export const CODES_ERREUR_FORMULAIRE = [
  'LIEN_INCONNU',
  'LIEN_CLOS',
  'SAISIE_INVALIDE',
  'FICHIER_TROP_GROS',
  'TYPE_FICHIER_REFUSE',
  'TROP_DE_SOUMISSIONS',
] as const;
export type CodeErreurFormulaire = (typeof CODES_ERREUR_FORMULAIRE)[number];

export const CODES_ERREUR = [
  'REQUETE_INVALIDE', 'JETON_ABSENT', 'JETON_INVALIDE', 'ROLE_INSUFFISANT', 'UTILISATEUR_DIFFERENT',
  'SESSION_INVALIDE', 'SESSION_EXPIREE', 'SESSION_REVOQUEE', 'COMPTE_INACTIF',
  'CONTACT_INTROUVABLE', 'CANDIDATURE_INTROUVABLE', 'BAREME_INTROUVABLE', 'NON_AUTEUR',
  'FICHE_ANONYMISEE', 'CANDIDAT_EMBAUCHE', 'CONTACT_CLOTURE', 'EVALUATION_EXISTANTE',
  'EVALUATION_HORS_ENTRETIEN', 'CHAMPS_RESERVES_APPEL', 'TRAME_RESERVEE_ENTRETIEN', 'LIEU_INCONNU',
  'CLOTURE_REFUSEE', 'EVALUATION_INVALIDE', 'BAREME_CHANGE',
  'HORODATAGE_FUTUR', 'HORODATAGE_AVANT_OUVERTURE', 'HORODATAGE_TROP_ANCIEN', 'ENVOI_DEJA_UTILISE',
  ...CODES_ERREUR_CV,
  ...CODES_ERREUR_FORMULAIRE,
  'PARAMETRE_INCONNU',
] as const;
export type CodeErreur = (typeof CODES_ERREUR)[number];

export interface DetailValidation {
  champ: string;
  message: string;
}

/** Barème sur lequel la note a été saisie, et celui qui s'applique à l'arrivée. */
export interface BaremeRefuse {
  affiche: { id: string; version: number | null };
  applicable: { id: string; version: number };
}

/** Corps de toute erreur codée (Nest le sérialise tel quel). */
export interface CorpsErreur {
  statusCode: number;
  error: string;
  code: CodeErreur;
  message: string;
  details?: DetailValidation[];
  bareme?: BaremeRefuse;
  /** COMPTE_INACTIF : compte du cookie présenté, dont le téléphone efface les saisies. */
  utilisateurId?: string;
}

/** Refus après lesquels le contenu de la saisie quitte aussitôt le téléphone. */
export const CODES_CONTENU_A_EFFACER: readonly CodeErreur[] = [
  'FICHE_ANONYMISEE',
  'CONTACT_INTROUVABLE',
  'CANDIDATURE_INTROUVABLE',
];

export function estCodeErreur(valeur: unknown): valeur is CodeErreur {
  return typeof valeur === 'string' && (CODES_ERREUR as readonly string[]).includes(valeur);
}

/** Messages fixes ; les autres se composent (fabriques ci-dessous) ou viennent des règles métier. */
export const MESSAGES_ERREUR = {
  REQUETE_INVALIDE: 'Requête invalide',
  JETON_ABSENT: 'Jeton absent',
  JETON_INVALIDE: 'Jeton invalide ou expiré',
  ROLE_INSUFFISANT: 'Rôle insuffisant',
  UTILISATEUR_DIFFERENT: 'Cette saisie a été faite sous un autre compte : elle ne part pas sous le vôtre.',
  SESSION_INVALIDE: 'Session invalide',
  SESSION_EXPIREE: 'Session expirée',
  SESSION_REVOQUEE: 'Session révoquée, reconnectez-vous',
  COMPTE_INACTIF: 'Compte inactif',
  CONTACT_INTROUVABLE: 'Contact introuvable',
  CANDIDATURE_INTROUVABLE: 'Candidature introuvable',
  NON_AUTEUR: 'Seul l’auteur de ce contact peut le compléter',
  FICHE_ANONYMISEE: 'Fiche anonymisée : ce contact ne se complète plus',
  CANDIDAT_EMBAUCHE:
    'Candidat embauché : la note d’entretien ne change plus, le suivi se poursuit sur sa fiche employé',
  CONTACT_CLOTURE: 'Contact clôturé : il ne se modifie plus, ouvrez un nouveau contact',
  EVALUATION_EXISTANTE: 'Cette évaluation est figée : un nouvel entretien crée une nouvelle évaluation',
  EVALUATION_HORS_ENTRETIEN: 'Une évaluation se rattache à un entretien présentiel',
  TRAME_RESERVEE_ENTRETIEN: 'La trame d’entretien est réservée au contact présentiel',
  LIEU_INCONNU: 'Lieu de rendez-vous inconnu',
  ENVOI_DEJA_UTILISE: 'Identifiant d’envoi déjà utilisé sur un autre contact',
  CV_FICHIER_ABSENT: 'Joindre le CV',
  CV_TROP_VOLUMINEUX: 'CV de plus de 10 Mo : enregistrez-le en PDF plus léger',
  CV_ENVOI_INVALIDE: 'Envoi interrompu ou invalide : relancez l’envoi du fichier',
  CV_DEJA_PRESENT: 'Ce CV est déjà sur cette candidature',
  DEPOT_CV_INTROUVABLE: 'CV déposé introuvable',
  DEPOT_CV_DEJA_TRAITE: 'Ce CV a déjà été rattaché, écarté ou effacé',
  DEPOT_CV_NON_RATTACHE: 'Ce CV n’est plus rattaché à cette candidature',
  COORDONNEE_NON_LUE: 'Ce téléphone ou cet e-mail ne figure pas dans le CV',
  COORDONNEE_DEJA_RENSEIGNEE: 'La fiche a déjà cette coordonnée : elle n’est pas remplacée',
  COORDONNEE_AUTRE_FICHE: 'Cette coordonnée est déjà celle d’une autre fiche',
  ANNONCE_NON_OUVERTE:
    'On ne crée une candidature que sur une annonce validée ou publiée : celle-ci est en brouillon, close ou archivée',
  PERSONNE_NE_PLUS_CONTACTER:
    'Cette personne a demandé à ne plus être contactée : sa fiche ne reçoit pas de nouvelle candidature',
  CANDIDATURE_EMBAUCHEE:
    'Ce candidat est passé employé : son dossier ne se supprime pas ici, il vit dans sa fiche employé',
  COORDONNEES_DEUX_FICHES:
    'Le téléphone et l’e-mail saisis désignent deux fiches différentes : n’en gardez qu’un, la fusion se fait à la main',
  PARAMETRE_INCONNU: 'Paramètre inconnu',
} as const satisfies Partial<Record<CodeErreur, string>>;

/** FICHE_ANONYMISEE sur les routes du dépôt de CV. */
export const MESSAGE_FICHE_ANONYMISEE_CV = 'Fiche anonymisée : on n’y rattache plus de CV';

/** CV_FORMAT_REFUSE : le message vient de MESSAGES_FORMAT_CV (format-cv.ts). */

/** EVALUATION_EXISTANTE quand deux validations du même entretien se croisent. */
export const MESSAGE_EVALUATION_CONCURRENTE = 'Cet entretien vient d’être évalué';

export function messageChampsReservesAppel(champs: readonly string[]): string {
  return (
    `Champs réservés à l’appel téléphonique : ${champs.join(', ')}. ` +
    'La pastille se pose au téléphone, la note après l’entretien.'
  );
}

export function messageBaremeIntrouvable(familleLibelle: string): string {
  return `Aucun barème actif pour le métier ${familleLibelle}`;
}

/**
 * La note a été saisie sur un barème qui ne s'applique plus : le téléphone la
 * fait revoir. Sans version affichée, ou à version égale (autre famille), le
 * message ne cite que la version applicable.
 */
export function messageBaremeChange(affiche: number | null, applicable: number): string {
  if (affiche !== null && affiche !== applicable) {
    return (
      `Barème modifié depuis l’entretien : notation faite sur la v${affiche}, ` +
      `la v${applicable} s’applique désormais. Revoyez la note puis validez à nouveau.`
    );
  }
  return (
    `Barème du métier modifié depuis l’entretien : la v${applicable} s’applique désormais. ` +
    'Revoyez la note puis validez à nouveau.'
  );
}
